/**
 * A generic full-screen modal overlay — the first of its kind in this app
 * (every other "floating panel" here is an anchored dropdown, e.g.
 * SettingsPanel.tsx / StaffLayout.tsx's NavGroup / TenantForm.tsx's lease
 * pill). Closes on Escape, on a backdrop click, or the explicit close
 * button — never on a click inside the panel itself.
 */

import { useEffect } from 'react';
import type { ReactNode } from 'react';

interface LightboxProps {
  title: string;
  onClose: () => void;
  children: ReactNode;
}

export function Lightbox({ title, onClose, children }: LightboxProps) {
  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  return (
    <div className="lightbox" role="dialog" aria-modal="true" aria-label={title} onClick={onClose}>
      <div className="lightbox__panel" onClick={(event) => event.stopPropagation()}>
        <div className="lightbox__header">
          <h2>{title}</h2>
          <button type="button" className="lightbox__close" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>
        <div className="lightbox__body">{children}</div>
      </div>
    </div>
  );
}
